import React, { useState, useEffect } from "react";
import { Modal, Form, Input, Button, Select, message, DatePicker, Divider, Space, Tooltip, Radio, Tag } from "antd";
import { CheckOutlined, InfoCircleOutlined, EditOutlined, ClockCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { UTCDatePicker, UTCTimePicker } from "../../../../components/DateTimeInput";
import { useTimeZone } from "../../../../contexts/TimeZoneContext";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import duration from "dayjs/plugin/duration";
import AircraftTag from "../../../../components/AircraftTag";
import AircraftSelector from "../../../../components/common/AircraftSelector";
import { flightOperationFlightService } from "../../../../api/enhancedApi";
import { FLIGHT_STATUS, FLIGHT_STATUS_LABELS, FLIGHT_STATUS_COLORS } from "../constants";
import { useAuth } from "../../../../hooks/useAuth";
import { createErrorHandler } from "../../../../utils/errorHandler";
import { prepareApiSubmit, COMMON_DATE_FIELDS } from "../../../../api/apiUtils";
import DiversionModal from "./DiversionModal";
import ConstraintsService from "../../../flightSchedule/ScheduleEditor/services/ConstraintsService";
import { formatTimeWithZone } from "../../../../utils/timezoneUtils";
import { getAirportConstraint } from "../../../../utils/airportCodeUtils";

dayjs.extend(utc);
dayjs.extend(duration);

/**
 * 航班信息编辑器组件 (V2.0)
 * 使用统一变更命令 `UPDATE_FLIGHT_INFO` 进行更新
 *
 * @param {Object} props.flight 航班对象
 * @param {Function} props.onSuccess 成功回调函数
 * @param {ReactNode} props.triggerComponent 自定义触发组件
 * @param {Function} props.onOpenEditor 打开编辑器前的回调函数
 */
const FlightInfoEditor = ({ flight, onSuccess, triggerComponent, onOpenEditor }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [visible, setVisible] = useState(false);
    const [diversionModalVisible, setDiversionModalVisible] = useState(false);
    const [airportOptions, setAirportOptions] = useState([]);
    const [aircraftOptions, setAircraftOptions] = useState([]);
    const [etaDateAnimating, setEtaDateAnimating] = useState(false);
    const [ataDateAnimating, setAtaDateAnimating] = useState(false);
    const [ataIcaoDateAnimating, setAtaIcaoDateAnimating] = useState(false);
    // 起降机场约束（宵禁规则、时区），用于编辑时的宵禁提示
    const [airportConstraints, setAirportConstraints] = useState({});
    const { timeZone } = useTimeZone();
    const { user } = useAuth();

    // 创建专用的错误处理器
    const handleFlightUpdateError = createErrorHandler("航班信息更新", {
        showNotification: true, // 使用notification显示详细错误
        duration: 6, // 显示更长时间
    });

    // 使用 useWatch 来获取 std 的值，以便传递给其他时间选择器
    const stdValue = Form.useWatch("std", form);

    // 使用 useWatch 监听表单中的时间字段，用于计算时间差
    const stdTime = Form.useWatch("std", form);
    const staTime = Form.useWatch("sta", form);
    const etdTime = Form.useWatch("etd", form);
    const etaTime = Form.useWatch("eta", form);
    const atdTime = Form.useWatch("atd", form);
    const ataTime = Form.useWatch("ata", form);
    const atdIcaoTime = Form.useWatch("atd_icao", form);
    const ataIcaoTime = Form.useWatch("ata_icao", form);
    // 监听飞机注册号：换飞机时，例外飞机的宵禁豁免也要跟着重新判定
    const aircraftReg = Form.useWatch("aircraft_reg", form);

    const TERMINAL_STATUSES = [FLIGHT_STATUS.CANCELLED, FLIGHT_STATUS.PLAN_CANCELLED, FLIGHT_STATUS.REPLACED];

    // 实时推断状态：监听四个实际时间字段变化，自动更新状态选择器
    // 只向上推断（到 DEPARTED/COMPLETED），不向下回退——避免编辑 ETD 等字段时误将 DELAYED 改回 SCHEDULED
    useEffect(() => {
        if (!visible) return;

        const currentStatus = form.getFieldValue("status");
        if (TERMINAL_STATUSES.includes(currentStatus)) return;

        const isValid = (v) => v && v.isValid();
        const departureSideHasValue = isValid(atdTime) || isValid(atdIcaoTime);
        const arrivalSideHasValue = isValid(ataTime) || isValid(ataIcaoTime);

        let inferredStatus = null;
        if (departureSideHasValue && arrivalSideHasValue) {
            inferredStatus = FLIGHT_STATUS.COMPLETED;
        } else if (departureSideHasValue) {
            inferredStatus = FLIGHT_STATUS.DEPARTED;
        }

        if (inferredStatus && inferredStatus !== currentStatus) {
            form.setFieldValue("status", inferredStatus);
        }
    }, [atdTime, atdIcaoTime, ataTime, ataIcaoTime, visible, form]);

    // 初始化表单数据
    useEffect(() => {
        if (visible && flight) {
            form.resetFields();
            form.setFieldsValue({
                flight_number: flight.flight_number,
                departure_airport: flight.departure_airport,
                arrival_airport: flight.arrival_airport,
                std: flight.std ? dayjs.utc(flight.std) : null, // 确保处理null值
                sta: flight.sta ? dayjs.utc(flight.sta) : null,
                etd: flight.etd ? dayjs.utc(flight.etd) : null,
                eta: flight.eta ? dayjs.utc(flight.eta) : null,
                atd: flight.atd ? dayjs.utc(flight.atd) : null,
                ata: flight.ata ? dayjs.utc(flight.ata) : null,
                atd_icao: flight.atd_icao ? dayjs.utc(flight.atd_icao) : null,
                ata_icao: flight.ata_icao ? dayjs.utc(flight.ata_icao) : null,
                aircraft_reg: flight.aircraft_reg,
                status: flight.status,
                remarks: flight.remarks,
            });
        }
    }, [visible, flight, form]);

    // 获取缓存的机场数据
    useEffect(() => {
        if (visible && window.cachedAirports && Array.isArray(window.cachedAirports) && window.cachedAirports.length > 0) {
            setAirportOptions(
                window.cachedAirports.map((airport) => ({
                    value: airport.iata_code,
                    label: `${airport.iata_code}${airport.icao_code ? ` - ${airport.icao_code}` : ""}${airport.airport_name ? ` (${airport.airport_name})` : ""}`,
                })),
            );
        }
    }, [visible]);

    // 获取缓存的飞机数据
    useEffect(() => {
        if (visible && window.cachedAircrafts && Array.isArray(window.cachedAircrafts) && window.cachedAircrafts.length > 0) {
            setAircraftOptions(
                window.cachedAircrafts.map((aircraft) => ({
                    value: aircraft.registration,
                    label: <AircraftTag registration={aircraft.registration} showType={true} />,
                })),
            );
        }
    }, [visible]);

    // T01: 编辑弹窗打开时加载起降机场约束（宵禁规则、时区）；失败时该服务会回退成空规则，不会误报宵禁
    useEffect(() => {
        if (!visible) return;
        const codes = [flight?.departure_airport, flight?.arrival_airport].filter(Boolean);
        if (codes.length === 0) return;

        let cancelled = false;
        ConstraintsService.loadAirportConstraints(codes, {
            setConstraintsLoading: () => {},
            setAirportConstraints: (map) => {
                if (!cancelled) setAirportConstraints(map || {});
            },
            setConstraintsError: () => {},
        });
        return () => {
            cancelled = true;
        };
    }, [visible, flight?.departure_airport, flight?.arrival_airport]);

    // 宵禁判定：与计划编排页同口径（按 UTC 判定，宵禁规则也按 UTC 存）。
    // 机场时区只用于把命中信息里的时间显示成当地时间。命中返回展示信息，未命中返回 null。
    const evaluateCurfew = (time, airportCode, operation, aircraftRegParam = aircraftReg) => {
        if (!time || typeof time.isValid !== "function" || !time.isValid() || !airportCode) {
            return null;
        }

        const result = ConstraintsService.checkCurfewViolation(time, airportCode, operation, {
            airportConstraints,
            aircraft_reg: aircraftRegParam || undefined,
        });
        if (!result || !result.isViolated) return null;

        const timezone = getAirportConstraint(airportConstraints, airportCode)?.timezone || "UTC";
        const opLabel = operation === "departure" ? "起飞" : "到达";
        const localTime = formatTimeWithZone(time.toISOString(), timezone, "MM-DD HH:mm");
        const nextAvailableLocal = result.nextAvailableTime
            ? formatTimeWithZone(new Date(result.nextAvailableTime).toISOString(), timezone, "MM-DD HH:mm")
            : null;

        return { airportCode, operation, opLabel, timezone, localTime, nextAvailableLocal };
    };

    // 渲染宵禁提示标签（命中才渲染），红色样式沿用计划编排页的“宵禁”标签
    const renderCurfewHint = (time, airportCode, operation) => {
        const hit = evaluateCurfew(time, airportCode, operation);
        if (!hit) return null;

        const tip = (
            <div>
                <div>
                    {hit.opLabel}时间 {hit.localTime} 落在 {hit.airportCode} 的{hit.opLabel}宵禁时段内
                </div>
                {hit.nextAvailableLocal && (
                    <div>
                        最早可{hit.opLabel}：{hit.nextAvailableLocal}
                    </div>
                )}
            </div>
        );

        return (
            <Tooltip title={tip}>
                <Tag icon={<WarningOutlined />} color="red" style={{ marginLeft: 8, cursor: "default", fontSize: "12px" }}>
                    宵禁
                </Tag>
            </Tooltip>
        );
    };

    // 处理 ETD 变化，自动计算 ETA
    const handleEtdChange = (time) => {
        if (!time || !time.isValid()) {
            form.setFieldValue("etd", null);
            return; // 清空或无效时间，直接设置并返回
        }

        // 获取计划起飞和到达时间
        const std = form.getFieldValue("std");
        const sta = form.getFieldValue("sta");

        // 计算计划时间的差值（分钟）
        if (std && std.isValid() && sta && sta.isValid()) {
            // 计算飞行时长（考虑可能的跨天情况）
            const stdTimeMinutes = std.hour() * 60 + std.minute();
            const staTimeMinutes = sta.hour() * 60 + sta.minute();

            // 计算时间差（分钟）
            let plannedDuration;
            if (staTimeMinutes >= stdTimeMinutes) {
                // 同一天内飞行
                plannedDuration = staTimeMinutes - stdTimeMinutes;
            } else {
                // 跨天飞行
                plannedDuration = 24 * 60 - stdTimeMinutes + staTimeMinutes;
            }

            if (plannedDuration > 0) {
                // 计算预计到达时间 = 预计起飞时间 + 计划飞行时长
                const calculatedEta = time.clone().add(plannedDuration, "minute");

                // 应用到表单，这会触发handleEtaChange来处理可能的日期调整
                form.setFieldValue("eta", calculatedEta);

                // 提示用户已自动计算
                message.info("已根据计划飞行时间自动计算预计到达时间");
            }
        }
    };

    // 处理 ETA 变化，自动调整日期
    const handleEtaChange = (time) => {
        if (!time || !time.isValid()) {
            form.setFieldValue("eta", null);
            return; // 清空或无效时间，直接设置并返回
        }
        const etd = form.getFieldValue("etd");
        if (!etd || !etd.isValid()) {
            form.setFieldValue("eta", time); // 没有ETD，无法比较，直接设置
            return;
        }

        // 记住原始时间的日期部分
        const originalDate = time.format("YYYY-MM-DD");

        let adjustedEta = time;
        let dateChanged = false;

        // 如果ETA的小时分钟早于ETD，则认为跨天，日期设置为ETD的日期加一天，时间部分保留time的
        if (time.format("HH:mm") < etd.format("HH:mm")) {
            adjustedEta = etd.add(1, "day").hour(time.hour()).minute(time.minute()).second(time.second()).millisecond(time.millisecond());
            dateChanged = true;
        }
        // 否则，如果日期不一致，将日期设置为ETD的日期，时间部分保留time的
        else if (!time.isSame(etd, "day")) {
            adjustedEta = etd.hour(time.hour()).minute(time.minute()).second(time.second()).millisecond(time.millisecond());
            dateChanged = true;
        }

        // 设置表单值
        form.setFieldValue("eta", adjustedEta);

        // 如果日期改变了，显示动画效果
        if (dateChanged && originalDate !== adjustedEta.format("YYYY-MM-DD")) {
            setEtaDateAnimating(true);
            setTimeout(() => {
                setEtaDateAnimating(false);
            }, 2000); // 2秒动画
        }
    };

    // 处理 ATA 变化，自动调整日期
    const handleAtaChange = (time) => {
        if (!time || !time.isValid()) {
            form.setFieldValue("ata", null);
            return; // 清空或无效时间，直接设置并返回
        }
        const atd = form.getFieldValue("atd");
        if (!atd || !atd.isValid()) {
            form.setFieldValue("ata", time); // 没有ATD，无法比较，直接设置
            return;
        }

        // 记住原始时间的日期部分
        const originalDate = time.format("YYYY-MM-DD");
        let adjustedAta = time;
        let dateChanged = false;

        // 如果 ATA 的小时分钟早于 ATD，则认为跨天，日期设置为 ATD 的日期加一天，时间部分保留 time 的
        if (time.format("HH:mm") < atd.format("HH:mm")) {
            adjustedAta = atd.add(1, "day").hour(time.hour()).minute(time.minute()).second(time.second()).millisecond(time.millisecond());
            dateChanged = true;
        }
        // 否则，如果日期不一致，将日期设置为 ATD 的日期，时间部分保留 time 的
        else if (!time.isSame(atd, "day")) {
            adjustedAta = atd.hour(time.hour()).minute(time.minute()).second(time.second()).millisecond(time.millisecond());
            dateChanged = true;
        }

        // 设置表单值
        form.setFieldValue("ata", adjustedAta);

        // 如果日期改变了，显示动画效果
        if (dateChanged && originalDate !== adjustedAta.format("YYYY-MM-DD")) {
            setAtaDateAnimating(true);
            setTimeout(() => {
                setAtaDateAnimating(false);
            }, 1500); // 1.5秒动画
        }
    };

    // 新增：处理 ATA ICAO 变化，自动调整日期
    const handleAtaIcaoChange = (time) => {
        if (!time || !time.isValid()) {
            form.setFieldValue("ata_icao", null);
            return; // 清空或无效时间，直接设置并返回
        }
        const atdIcao = form.getFieldValue("atd_icao");
        if (!atdIcao || !atdIcao.isValid()) {
            form.setFieldValue("ata_icao", time); // 没有 ATD ICAO，无法比较，直接设置
            return;
        }

        // 记住原始时间的日期部分
        const originalDate = time.format("YYYY-MM-DD");
        let adjustedAtaIcao = time;
        let dateChanged = false;

        // 如果 ATA ICAO 的小时分钟早于 ATD ICAO，则认为跨天，日期设置为 ATD ICAO 的日期加一天，时间部分保留 time 的
        if (time.format("HH:mm") < atdIcao.format("HH:mm")) {
            adjustedAtaIcao = atdIcao.add(1, "day").hour(time.hour()).minute(time.minute()).second(time.second()).millisecond(time.millisecond());
            dateChanged = true;
        }
        // 否则，如果日期不一致，将日期设置为 ATD ICAO 的日期，时间部分保留 time 的
        else if (!time.isSame(atdIcao, "day")) {
            adjustedAtaIcao = atdIcao.hour(time.hour()).minute(time.minute()).second(time.second()).millisecond(time.millisecond());
            dateChanged = true;
        }

        // 设置表单值
        form.setFieldValue("ata_icao", adjustedAtaIcao);

        // 如果日期改变了，显示动画效果
        if (dateChanged && originalDate !== adjustedAtaIcao.format("YYYY-MM-DD")) {
            setAtaIcaoDateAnimating(true);
            setTimeout(() => {
                setAtaIcaoDateAnimating(false);
            }, 1500); // 1.5秒动画
        }
    };

    // 实际提交：构建变更命令并发送（宵禁二次确认通过后走这里）
    const submitCore = async (values) => {
        try {
            setLoading(true);

            // 准备修改的数据 (flightInfoDetails 对象)
            const flightInfoDetails = {};

            // 航班号
            if (values.flight_number !== flight.flight_number) {
                flightInfoDetails.flight_number = values.flight_number;
            }

            // 航段
            if (values.departure_airport !== flight.departure_airport) {
                flightInfoDetails.departure_airport = values.departure_airport;
            }
            if (values.arrival_airport !== flight.arrival_airport) {
                flightInfoDetails.arrival_airport = values.arrival_airport;
            }

            // 航班时间 (转换为 ISO 8601 UTC 字符串)
            ["std", "sta", "etd", "eta", "atd", "ata", "atd_icao", "ata_icao"].forEach((field) => {
                const formValueDayjs = values[field]; // Dayjs object or null
                const flightValueDayjs = flight[field] ? dayjs.utc(flight[field]) : null;

                // Format Dayjs objects to ISO 8601 UTC string for comparison and API submission
                const formValueString = formValueDayjs ? formValueDayjs.utc().format() : null;
                const flightValueString = flightValueDayjs ? flightValueDayjs.utc().format() : null;

                if (formValueString !== flightValueString) {
                    flightInfoDetails[field] = formValueString; // Send ISO string or null
                }
            });

            // 飞机注册号
            if (values.aircraft_reg !== flight.aircraft_reg) {
                flightInfoDetails.aircraft_reg = values.aircraft_reg;
            }

            // 航班状态
            if (values.status !== flight.status) {
                flightInfoDetails.status = values.status;
            }

            // 备注 (如果备注字段有变化，或者其他字段有变化但备注字段非空)
            const remarksChanged = values.remarks !== flight.remarks;
            if (remarksChanged) {
                flightInfoDetails.remarks = values.remarks; // 即使是空字符串也要传递
            }

            // 检查是否有变更
            if (Object.keys(flightInfoDetails).length === 0) {
                message.info("未检测到任何变更");
                setVisible(false);
                return;
            }

            // 使用通用工具函数来准备提交的数据
            const preparedDetails = prepareApiSubmit(flightInfoDetails, COMMON_DATE_FIELDS);

            // 构建变更命令 V2.0
            const commandData = {
                flight_operation_id: Number(flight.id), // 后端期望单个ID
                change_type: "UPDATE_FLIGHT_INFO",
                payload: {
                    flightInfoDetails: preparedDetails, // 使用处理后的数据
                },
                operator: user.id, // 直接传递user ID值，不使用对象结构
                trigger_source: "FLIGHT_INFO_EDITOR",
                timestamp: new Date().toISOString(),
            };

            // 发送请求，使用更新后的服务，并标记为静默错误处理
            const response = await flightOperationFlightService.applyChanges(commandData, {
                _silentError: true, // 标记为静默处理，避免axios拦截器显示错误
            });

            // 根据后端响应格式判断成功与否 - 检查批量操作的详细结果
            if (response.data?.success) {
                // 检查是否有失败的项目
                const responseData = response.data.data;
                if (responseData && responseData.failed_items && responseData.failed_items.length > 0) {
                    // 有失败项目，从失败项目中查找当前航班
                    const failedItem = responseData.failed_items.find((item) => item.flight_id === flight.id);
                    if (failedItem) {
                        const errorMessage = failedItem.error || "更新失败";
                        throw new Error(errorMessage);
                    }
                }

                // 操作成功
                message.success(`已更新航班 ${flight.flight_number || flight.id} 的信息`);
                setVisible(false);
                if (onSuccess) {
                    onSuccess(flight.id, response.data.data); // 可以传递更新后的数据（如果后端返回）
                }
            } else {
                throw new Error(response.data?.message || "更新失败");
            }
        } catch (error) {
            // 使用统一的错误处理器
            handleFlightUpdateError(error);
        } finally {
            setLoading(false);
        }
    };

    // 处理表单提交：先校验、状态检查；命中宵禁则先二次确认，确认后再交给 submitCore
    const handleSubmit = async () => {
        try {
            // 验证用户身份
            if (!user || !user.id) {
                message.error("用户未登录或身份验证失效，请重新登录");
                setVisible(false);
                return;
            }

            const values = await form.validateFields();

            const finalStatus = values.status;
            const departureSideHasValue =
                (values.atd && dayjs(values.atd).isValid()) || (values.atd_icao && dayjs(values.atd_icao).isValid());
            const arrivalSideHasValue =
                (values.ata && dayjs(values.ata).isValid()) || (values.ata_icao && dayjs(values.ata_icao).isValid());

            if (finalStatus === FLIGHT_STATUS.COMPLETED && !arrivalSideHasValue) {
                message.error("航班状态为'已完成'，但缺少实际到达时间(ATA/ATA Touchdown)，请补充完整");
                return;
            }

            if (finalStatus === FLIGHT_STATUS.DEPARTED && !departureSideHasValue) {
                message.error("航班状态为'已起飞'，但缺少实际起飞时间(ATD)或起飞时间(ATD Airborne)，请补充完整");
                return;
            }

            // T07: 保存前若 ETD / ETA 仍落在宵禁时段内，先弹二次确认，确认后才提交
            const curfewConflicts = [];
            const depHit = evaluateCurfew(values.etd, flight?.departure_airport, "departure", values.aircraft_reg);
            if (depHit) curfewConflicts.push({ label: "预计起飞 (ETD)", hit: depHit });
            const arrHit = evaluateCurfew(values.eta, flight?.arrival_airport, "arrival", values.aircraft_reg);
            if (arrHit) curfewConflicts.push({ label: "预计到达 (ETA)", hit: arrHit });

            if (curfewConflicts.length > 0) {
                Modal.confirm({
                    title: "部分时间仍在宵禁时段内，确认保存？",
                    icon: <WarningOutlined style={{ color: "#faad14" }} />,
                    width: 480,
                    content: (
                        <div>
                            <div style={{ marginBottom: 8 }}>以下时间落在机场宵禁时段内：</div>
                            <ul style={{ paddingLeft: 18, margin: 0 }}>
                                {curfewConflicts.map((c) => (
                                    <li key={c.label}>
                                        {c.label}：{c.hit.localTime} 落在 {c.hit.airportCode} 的{c.hit.opLabel}宵禁时段内
                                        {c.hit.nextAvailableLocal ? `（最早可${c.hit.opLabel} ${c.hit.nextAvailableLocal}）` : ""}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    ),
                    okText: "仍然保存",
                    okButtonProps: { danger: true },
                    cancelText: "返回修改",
                    onOk: () => submitCore(values),
                });
                return;
            }

            submitCore(values);
        } catch (error) {
            // 表单校验失败等异常
            handleFlightUpdateError(error);
        }
    };

    // 渲染表单内容
    const renderFormContent = () => (
        <Form form={form} layout="vertical" autoComplete="off">
            <div style={{ display: "flex", flexWrap: "wrap", gap: "8px 16px" }}>
                {/* Row 1: STD, STA */}
                <Form.Item name="std" label="计划起飞 (STD)" rules={[{ required: true }]} style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}>
                    <UTCTimePicker showDate={true} format="HH:mm" style={{ width: "100%" }} disabled={true} />
                </Form.Item>
                <Form.Item
                    name="sta"
                    label={
                        <div style={{ display: "flex", alignItems: "center" }}>
                            计划到达 (STA)
                            <TimeDifferenceDisplay startTime={stdTime} endTime={staTime} label="计划飞行时间" type="plan" />
                        </div>
                    }
                    rules={[{ required: true }]}
                    style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}
                >
                    <UTCTimePicker showDate={true} format="HH:mm" style={{ width: "100%" }} disabled={true} />
                </Form.Item>

                {/* Row 2: ETD, ETA */}
                <Form.Item
                    name="etd"
                    label={
                        <div style={{ display: "flex", alignItems: "center" }}>
                            预计起飞 (ETD)
                            {renderCurfewHint(etdTime, flight?.departure_airport, "departure")}
                        </div>
                    }
                    style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}
                >
                    <UTCTimePicker showDate={true} format="HH:mm" style={{ width: "100%" }} defaultDate={stdValue} minuteStep={1} onChange={handleEtdChange} />
                </Form.Item>
                <Form.Item
                    name="eta"
                    label={
                        <div style={{ display: "flex", alignItems: "center" }}>
                            预计到达 (ETA)
                            <TimeDifferenceDisplay startTime={etdTime} endTime={etaTime} label="预计飞行时间" type="estimate" />
                            {renderCurfewHint(etaTime, flight?.arrival_airport, "arrival")}
                        </div>
                    }
                    style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}
                >
                    <UTCTimePicker showDate={true} format="HH:mm" style={{ width: "100%" }} allowClear defaultDate={stdValue} minuteStep={1} onChange={handleEtaChange} dateAnimating={etaDateAnimating} />
                </Form.Item>

                {/* Row 3: ATD, ATA */}
                <Form.Item name="atd" label="实际起飞 (ATD)" style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}>
                    <UTCTimePicker showDate={true} format="HH:mm" style={{ width: "100%" }} allowClear defaultDate={stdValue} minuteStep={1} />
                </Form.Item>
                <Form.Item
                    name="ata"
                    label={
                        <div style={{ display: "flex", alignItems: "center" }}>
                            实际到达 (ATA)
                            <TimeDifferenceDisplay startTime={atdTime} endTime={ataTime} label="实际飞行时间" type="actual" />
                        </div>
                    }
                    style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}
                >
                    <UTCTimePicker showDate={true} format="HH:mm" style={{ width: "100%" }} allowClear defaultDate={stdValue} minuteStep={1} onChange={handleAtaChange} dateAnimating={ataDateAnimating} />
                </Form.Item>

                <Form.Item name="atd_icao" label="ATD Airborne" style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}>
                    <UTCTimePicker showDate={true} format="HH:mm" style={{ width: "100%" }} allowClear defaultDate={stdValue} minuteStep={1} />
                </Form.Item>
                <Form.Item
                    name="ata_icao"
                    label={
                        <div style={{ display: "flex", alignItems: "center" }}>
                            ATA Touchdown
                            <TimeDifferenceDisplay startTime={atdIcaoTime} endTime={ataIcaoTime} label="空中飞行时间" type="airborne" />
                        </div>
                    }
                    style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}
                >
                    <UTCTimePicker showDate={true} format="HH:mm" style={{ width: "100%" }} allowClear defaultDate={stdValue} minuteStep={1} onChange={handleAtaIcaoChange} dateAnimating={ataIcaoDateAnimating} />
                </Form.Item>

                {/* Row 4: Departure/Arrival Airport */}
                {/* <Form.Item name="departure_airport" label="出发机场" rules={[{ required: true }]} style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}>
                    <Tooltip title="只能通过发布航班计划的方式修改">
                        <Select showSearch placeholder="选择出发机场" options={airportOptions} filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} disabled />
                    </Tooltip>
                </Form.Item>
                <Form.Item name="arrival_airport" label="到达机场" rules={[{ required: true }]} style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}>
                    <Tooltip title="只能通过发布航班计划的方式修改">
                        <Select showSearch placeholder="选择到达机场" options={airportOptions} filterOption={(input, option) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase())} disabled />
                    </Tooltip>
                </Form.Item> */}

                {/* Row 5: Aircraft Reg, Status */}
                <Form.Item name="aircraft_reg" label="飞机注册号" style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}>
                    <AircraftSelector allowClear placeholder="选择或搜索飞机注册号" valueType="registration" displayMode="detailed" />
                </Form.Item>
                <Form.Item name="status" label="航班状态" style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}>
                    <FlightStatusSelector form={form} onDiversionClick={() => {
                        // 备降为结构性变更（生成衍生航段），不沿用字段更新；
                        // 关闭编辑器后打开专用备降表单 Modal，最终由 PROCESS_DIVERSION 命令处理
                        setVisible(false);
                        setDiversionModalVisible(true);
                    }} />
                </Form.Item>

                {/* Row 6: Flight Number, Remarks */}
                <Form.Item name="flight_number" label="航班号" rules={[{ required: true, message: "请输入航班号" }]} style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}>
                    <Input placeholder="如: CA123" maxLength={7} />
                </Form.Item>
                <Form.Item name="remarks" label="备注" style={{ width: "calc(50% - 8px)", marginBottom: "16px" }}>
                    <Input.TextArea rows={2} placeholder="输入航班备注信息" />
                </Form.Item>
            </div>
        </Form>
    );

    // 自定义触发器或默认按钮
    const trigger = triggerComponent ? (
        React.cloneElement(triggerComponent, {
            onClick: (e) => {
                e.stopPropagation();
                // 先调用 onOpenEditor 关闭 Popover
                if (onOpenEditor) {
                    onOpenEditor();
                }
                // 再打开 Modal
                setVisible(true);
            },
        })
    ) : (
        <Button
            type="link"
            icon={<EditOutlined />}
            size="small"
            onClick={(e) => {
                e.stopPropagation();
                // 先调用 onOpenEditor 关闭 Popover
                if (onOpenEditor) {
                    onOpenEditor();
                }
                // 再打开 Modal
                setVisible(true);
            }}
        />
    );

    return (
        <>
            {trigger}

            <Modal
                title={
                    <div>
                        编辑航班信息
                        <span style={{ color: "#888", marginLeft: "10px", fontSize: "14px" }}>
                            {flight?.flight_number} ({flight?.departure_airport} - {flight?.arrival_airport})
                        </span>
                    </div>
                }
                open={visible}
                onCancel={() => setVisible(false)}
                width={700}
                footer={[
                    <Button key="cancel" onClick={() => setVisible(false)}>
                        取消
                    </Button>,
                    <Button key="submit" type="primary" loading={loading} onClick={handleSubmit} icon={<CheckOutlined />}>
                        保存变更
                    </Button>,
                ]}
                destroyOnHidden
                maskClosable={false}
                centered
            >
                {renderFormContent()}
            </Modal>

            <DiversionModal
                open={diversionModalVisible}
                onClose={() => setDiversionModalVisible(false)}
                flight={flight}
                onSuccess={(flightId, responseData) => {
                    // 备降成功后关闭编辑器并回调刷新
                    setVisible(false);
                    setDiversionModalVisible(false);
                    if (onSuccess) {
                        onSuccess(flightId, responseData);
                    }
                }}
            />
        </>
    );
};

const FlightStatusSelector = ({ form, onDiversionClick }) => {
    const currentStatus = Form.useWatch("status", form);

    // 监听时间字段以进行状态校验
    const atdTime = Form.useWatch("atd", form);
    const ataTime = Form.useWatch("ata", form);
    const atdIcaoTime = Form.useWatch("atd_icao", form);
    const ataIcaoTime = Form.useWatch("ata_icao", form);

    const handleStatusClick = (status) => {
        // 获取当前表单中的相关时间字段
        const hasActualDeparture = atdTime && atdTime.isValid();
        const hasActualArrival = ataTime && ataTime.isValid();
        const hasIcaoDeparture = atdIcaoTime && atdIcaoTime.isValid();
        const hasIcaoArrival = ataIcaoTime && ataIcaoTime.isValid();

        // 状态校验逻辑
        let canChangeStatus = true;
        let errorMessage = "";

        switch (status) {
            case FLIGHT_STATUS.DEPARTED:
                if (!hasActualDeparture && !hasIcaoDeparture) {
                    canChangeStatus = false;
                    errorMessage = "设置为'已起飞'状态需要先填写实际起飞时间(ATD)或起飞空中时间(ATD Airborne)";
                }
                break;
            case FLIGHT_STATUS.COMPLETED:
                if (!hasActualDeparture && !hasActualArrival) {
                    canChangeStatus = false;
                    errorMessage = "设置为'已完成'状态需要填写实际起飞(ATD)和实际到达(ATA)时间";
                } else if (!hasActualArrival) {
                    canChangeStatus = false;
                    errorMessage = "设置为'已完成'状态需要填写实际到达时间(ATA)";
                }
                break;
            case FLIGHT_STATUS.DIVERTED:
                if (!hasActualDeparture) {
                    canChangeStatus = false;
                    errorMessage = "设置为'备降'状态需要先填写实际起飞时间(ATD)";
                }
                break;
            case FLIGHT_STATUS.CANCELLED:
                if (hasActualDeparture || hasIcaoDeparture) {
                    canChangeStatus = false;
                    errorMessage = "航班状态为'已起飞'或'起飞空中时间(ATD Airborne)'，不能设置为'已取消'";
                }
                break;
            default:
                // 其他状态不做特殊限制
                break;
        }

        if (!canChangeStatus) {
            message.warning(errorMessage);
            return;
        }

        // 备降为航段结构变更（§8.2 PROCESS_DIVERSION），不直接修改状态字段，
        // 转入专用备降表单 Modal 录入备降机场/续飞段 STD 并生成衍生航段
        if (status === FLIGHT_STATUS.DIVERTED) {
            if (onDiversionClick) {
                onDiversionClick();
            } else {
                message.warning("备降入口未就绪，请刷新页面后重试");
            }
            return;
        }

        // 状态变更确认
        if (status === FLIGHT_STATUS.CANCELLED) {
            Modal.confirm({
                title: "确认取消航班?",
                content: '设置为"已取消"状态后,此航班将被标记为已取消。确定要继续吗?',
                okText: "确认",
                cancelText: "取消",
                onOk: () => {
                    form.setFieldValue("status", status);
                },
            });
        } else if (status === FLIGHT_STATUS.COMPLETED && !hasActualArrival && hasIcaoArrival) {
            // 特殊情况：有ICAO到达时间但没有ATA时间
            Modal.confirm({
                title: "自动设置实际到达时间?",
                content: "检测到已填写触地时间(ATA Touchdown)但未填写实际到达时间(ATA)。是否使用触地时间自动设置ATA?",
                okText: "自动设置",
                cancelText: "仅切换状态",
                onOk: () => {
                    form.setFieldValue("ata", ataIcaoTime);
                    form.setFieldValue("status", status);
                },
                onCancel: () => {
                    form.setFieldValue("status", status);
                },
            });
        } else {
            form.setFieldValue("status", status);
        }
    };

    // 定义状态显示顺序
    const statusOrder = [FLIGHT_STATUS.SCHEDULED, FLIGHT_STATUS.DELAYED, FLIGHT_STATUS.DEPARTED, FLIGHT_STATUS.COMPLETED, FLIGHT_STATUS.CANCELLED, FLIGHT_STATUS.DIVERTED];

    const MAX_BUTTONS_PER_ROW = 4; // 每行最多显示4个按钮

    // 过滤有效状态
    const validStatusList = statusOrder.filter((status) => status && FLIGHT_STATUS_LABELS[status]);
    // 按每行最大数分组
    const rows = [];
    for (let i = 0; i < validStatusList.length; i += MAX_BUTTONS_PER_ROW) {
        rows.push(validStatusList.slice(i, i + MAX_BUTTONS_PER_ROW));
    }

    // 状态按钮的提示文本
    const getStatusTooltip = (status) => {
        switch (status) {
            case FLIGHT_STATUS.SCHEDULED:
                return "计划中 - 航班按照计划运行";
            case FLIGHT_STATUS.DELAYED:
                return "延误 - 航班已延误";
            case FLIGHT_STATUS.DEPARTED:
                return "已起飞 - 需填写实际起飞时间(ATD)";
            case FLIGHT_STATUS.COMPLETED:
                return "已完成 - 需填写实际起飞(ATD)、实际离地(ATD Airborne)、实际着陆(ATA Touchdown)和实际到达(ATA)时间";
            case FLIGHT_STATUS.CANCELLED:
                return "已取消 - 航班已取消";
            case FLIGHT_STATUS.DIVERTED:
                return "备降 - 航班已备降至其他机场";
            default:
                return "";
        }
    };

    return (
        <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
            {rows.map((row, rowIdx) => (
                <div
                    key={rowIdx}
                    style={{
                        display: "flex",
                        flexDirection: "row",
                        gap: "8px",
                        flexWrap: "wrap",
                    }}
                >
                    {row.map((status) => {
                        const isActive = currentStatus === status;
                        return (
                            <Tooltip key={status} title={getStatusTooltip(status)}>
                                <Button
                                    type={isActive ? "primary" : "default"}
                                    ghost={isActive}
                                    size="small"
                                    onClick={() => handleStatusClick(status)}
                                    style={{
                                        textAlign: "center",
                                        padding: "0 12px",
                                        minWidth: "auto", // 让按钮宽度自适应内容
                                        flex: "none", // 不拉伸
                                    }}
                                >
                                    {isActive && <CheckOutlined />}
                                    {FLIGHT_STATUS_LABELS[status]}
                                </Button>
                            </Tooltip>
                        );
                    })}
                </div>
            ))}
        </div>
    );
};

/**
 * 计算并格式化两个时间点之间的时间差
 * @param {dayjs} startTime 开始时间
 * @param {dayjs} endTime 结束时间
 * @returns {Object} 包含可读格式和分钟数的对象，如果计算无效则返回null
 */
const calculateTimeDifference = (startTime, endTime) => {
    if (!startTime || !endTime || !startTime.isValid() || !endTime.isValid()) {
        return null;
    }

    // 确保使用UTC时间比较
    const start = startTime.utc();
    const end = endTime.utc();

    // 计算时间差（毫秒）
    const diffMs = end.diff(start);
    if (diffMs < 0) {
        // 如果时间差为负数（可能是跨天情况），返回无效
        return null;
    }

    // 转换为dayjs持续时间对象
    const diffDuration = dayjs.duration(diffMs);

    // 计算总分钟数
    const totalMinutes = Math.floor(diffMs / (1000 * 60));

    // 格式化为小时和分钟
    const hours = Math.floor(totalMinutes / 60);
    const minutes = totalMinutes % 60;

    // 构建可读格式
    const formattedDiff = `${hours}h ${minutes}m`;

    return {
        formatted: formattedDiff,
        minutes: totalMinutes,
    };
};

/**
 * 时间差显示组件
 */
const TimeDifferenceDisplay = ({ startTime, endTime, label, type = "default" }) => {
    const diff = calculateTimeDifference(startTime, endTime);

    if (!diff) {
        return null;
    }

    // 根据类型确定颜色
    let color = "blue";
    switch (type) {
        case "plan":
            color = "geekblue";
            break;
        case "estimate":
            color = "cyan";
            break;
        case "actual":
            color = "green";
            break;
        case "airborne":
            color = "purple";
            break;
        default:
            color = "blue";
    }

    return (
        <Tooltip title={`${label}: ${diff.minutes} 分钟`}>
            <Tag
                icon={<ClockCircleOutlined />}
                color={color}
                style={{
                    marginLeft: "8px",
                    cursor: "default",
                    fontSize: "12px",
                }}
            >
                {diff.formatted}
            </Tag>
        </Tooltip>
    );
};

export default FlightInfoEditor;
